import { useEffect, useState } from 'react';

import { getStatus } from '../lib/signup-api.js';

interface ProvisioningStatusProps {
  signupId: string;
  pollMs?: number;
}

export function ProvisioningStatus({ signupId, pollMs = 2000 }: ProvisioningStatusProps) {
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function poll(): Promise<void> {
      if (cancelled) return;
      try {
        const { status: s } = await getStatus(signupId);
        if (!cancelled) {
          setStatus(s);
          // On a terminal state, mark cancelled so future interval ticks no-op.
          // The interval itself is cleared in the effect cleanup below.
          if (s === 'ready' || s === 'failed') {
            cancelled = true;
          }
        }
      } catch {
        // Transient network error — try again on the next tick.
      }
    }

    // Kick off an immediate poll, then continue on the interval.
    void poll();
    const timerId = setInterval(() => void poll(), pollMs);

    return () => {
      // Prevent any in-flight request from calling setStatus after unmount.
      cancelled = true;
      clearInterval(timerId);
    };
  }, [signupId, pollMs]);

  if (status === 'ready') {
    return (
      <div className="text-center py-10">
        <p className="text-2xl font-semibold text-gray-900">Check your email to log in</p>
        <p className="mt-2 text-gray-500">
          We sent you a sign-in link — it will arrive in your inbox shortly.
        </p>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="text-center py-10">
        <p className="text-2xl font-semibold text-red-600">Something went wrong</p>
        <p className="mt-2 text-gray-500">Please contact support and we will get you sorted out.</p>
      </div>
    );
  }

  // Covers null (before first poll), 'paid', 'provisioning', and any unknown status.
  return (
    <div className="text-center py-10">
      <p className="text-xl text-gray-700">Setting up your community…</p>
      <p className="mt-2 text-sm text-gray-400">This usually takes less than a minute.</p>
    </div>
  );
}
